import { useState, useEffect } from "react";
import { ScopeProvider } from "../../../../scopes/ScopeProvider";
import AddUserWizard from "../../../common/userUtils/AddUserWizard";
import InviteAgentForm from "./InviteAgentForm.jsx";
import { UsersApi } from "../../../../api/common/apiIndex";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const MODES = [
  {
    key: "create",
    label: "Create user",
    description: "Enter full details and set credentials now.",
  },
  {
    key: "invite",
    label: "Invite agent",
    description: "Send a secure link so they finish the setup.",
  },
];

export default function BusinessAddUserPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(() => (searchParams.get("mode") === "invite" ? "invite" : "create"));
  const backTo = location.state?.backTo;

  useEffect(() => {
    const incoming = searchParams.get("mode") === "invite" ? "invite" : "create";
    setMode(incoming);
  }, [searchParams]);

  const scopeValue = {
    createUser: UsersApi.create,
    attachExistingUser: UsersApi.attachExistingUser,
    invalidateOnCreate: (client) => {
      client.invalidateQueries({ queryKey: ["agents"] });
      client.invalidateQueries({ queryKey: ["transactions"] });
    },
    routeAfterDone: (roles) => {
      if (backTo) return backTo;
      return Array.isArray(roles) && roles.includes("AGENT") ? "/home/agents" : "/home";
    },
    defaultRoles: ["AGENT"],
    navigate: (path) => navigate(path),
  };

  return (
    <ScopeProvider value={scopeValue}>
      <div className="container py-3">
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div>
              <h1 className="h5 mb-1">Add team members</h1>
              <p className="text-muted mb-0">Create an account yourself or email an invite.</p>
            </div>
            <div className="btn-group" role="group" aria-label="User creation mode">
              {MODES.map((option) => {
                const active = mode === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setMode(option.key)}
                    aria-pressed={active}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="card-footer bg-body-tertiary border-0">
            <div className="d-flex gap-4 flex-wrap">
              {MODES.map((option) => (
                <div key={option.key} className="text-muted small">
                  <strong className="d-block text-body">{option.label}</strong>
                  {option.description}
                </div>
              ))}
            </div>
          </div>
        </div>

        {mode === "create" ? (
          <AddUserWizard />
        ) : (
          <InviteAgentForm />
        )}
      </div>
    </ScopeProvider>
  );
}
